import { useRecentWorkspaceFlowSessions } from "@/api/flow_sessions";
import { ActionRow } from "@/components/rail-kit/ActionRow";
import { Accordion } from "@/components/ui/accordion";
import { useActiveChatFlowSessionId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useActiveChatFlowSessionId";
import { useChatsRailActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useChatsRailActions";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import { WorkspacesRailChatNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatNode";
import type { InboxItem } from "@cohesive/api-client";
import { MessagesSquare, Pin, Plus } from "lucide-react";
import { useMemo } from "react";

/**
 * The all-chats rail view: one chronological stream (chats read like a
 * history, not an org chart — the workspace pages keep the foldered views),
 * with a Pinned group on top. Creation lives in the rail head's chat verb
 * and, like every other rail's list, in the section's own closing verb.
 * Chats are PINNED rather than favourited — you pin a conversation to keep it
 * to hand; favouriting one reads oddly, and the rest of the app keeps the
 * favourite vocabulary for canvases, files and skills.
 */
export function ChatsAllRail() {
  const { data: recentChats = [] } = useRecentWorkspaceFlowSessions(50);
  const activeFlowSessionId = useActiveChatFlowSessionId();
  const {
    onOpen,
    onToggleStar,
    onRename,
    onDelete,
    onCreateChat,
    isCreatingChat,
    canCreateChat,
  } = useChatsRailActions();

  const starred = useMemo(
    () => recentChats.filter((c) => c.is_starred),
    [recentChats],
  );

  const renderChat = (chat: InboxItem) => (
    <WorkspacesRailChatNode
      key={chat.id}
      item={chat}
      active={activeFlowSessionId === chat.flow_session_id}
      onOpen={onOpen}
      onToggleStar={onToggleStar}
      onRename={onRename}
      onDelete={onDelete}
    />
  );

  return (
    // Padding belongs to the scroller inside, never to the flex item itself:
    // a border-box element can't shrink below its own padding, which would
    // floor this rail and stop the drawer short of where the other rails
    // let it reach.
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-onboarding-target="rail-view-chats"
    >
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2 pt-3">
        <Accordion
          type="multiple"
          defaultValue={["chats-all"]}
          className="flex flex-col gap-2"
        >
          {starred.length > 0 && (
            <RailGroup
              groupId="chats-starred"
              label="Pinned"
              icon={Pin}
              noCap
              items={starred.map((chat) => renderChat(chat))}
            />
          )}
          <RailGroup
            groupId="chats-all"
            label="Chats"
            icon={MessagesSquare}
            noCap
            items={[
              // The list's own closing verb rides INSIDE the section so it
              // collapses with it — the same row Skills and Connectors end
              // on, and the Workspaces run's "New workspace". It is the
              // section's one create control, so it stays under the quiet
              // zero-state line when the list is empty.
              ...(recentChats.length > 0
                ? recentChats.map((chat) => renderChat(chat))
                : [
                    <RailZeroState
                      key="chats-empty"
                      title={railZeroStateCopy.chats.all.title}
                      description={railZeroStateCopy.chats.all.description}
                    />,
                  ]),
              ...(canCreateChat
                ? [
                    <ActionRow
                      key="new-chat"
                      icon={Plus}
                      label="New chat"
                      align="row"
                      disabled={isCreatingChat}
                      onClick={() => onCreateChat()}
                    />,
                  ]
                : []),
            ]}
          />
        </Accordion>
      </div>
    </div>
  );
}
